import React, {useCallback, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import Workspace from "../components/Workspace";
import ReviewNotice from "../components/ReviewNotice";
import StatusBadge from "../components/StatusBadge";
import PhotoManager from "../components/PhotoManager";
import DocumentUpload from "../components/DocumentUpload";
import {ChipPicker, Field, Section, numberOrNull} from "../components/Fields";
import UnitTypes from "../components/UnitTypes";
import {COMMON_AMENITIES, POSSESSION} from "../constants";
import {createProject, getMyProject, submitProject, updateProject} from "../api/projects";
import {parseCoordinates, validateProject} from "../validation";

const EMPTY = {
    name: "", developerName: "", location: "", address: "", coordinates: "", description: "", reraNumber: "",
    kathaStatus: "", totalProjectArea: "", blockCount: "", totalFloors: "", liftCount: "", totalUnits: "",
    possessionStatus: "", possessionDate: "", amenities: [], photos: [], videoUrl: "", brochureFile: "", brokerage: "",
};

const toForm = (project) => {
    const merged = {...project, ...(project.pendingChanges || {})};
    const out = {...EMPTY};
    Object.keys(EMPTY).forEach((k) => {
        if (merged[k] !== null && merged[k] !== undefined) out[k] = Array.isArray(merged[k]) ? merged[k] : String(merged[k]);
    });
    out.photos = [merged.coverImage, ...(merged.images || [])].filter(Boolean);
    out.coordinates = merged.latitude != null && merged.longitude != null ? `${merged.latitude}, ${merged.longitude}` : "";
    return out;
};

const toPayload = (f) => {
    const text = (v) => (v && String(v).trim()) || null;
    const coords = parseCoordinates(f.coordinates);
    return {
        name: text(f.name), developerName: text(f.developerName), location: text(f.location), address: text(f.address),
        latitude: coords?.[0] ?? null, longitude: coords?.[1] ?? null, description: text(f.description),
        reraNumber: text(f.reraNumber), kathaStatus: text(f.kathaStatus), totalProjectArea: text(f.totalProjectArea),
        blockCount: numberOrNull(f.blockCount), totalFloors: numberOrNull(f.totalFloors), liftCount: numberOrNull(f.liftCount),
        totalUnits: numberOrNull(f.totalUnits), possessionStatus: text(f.possessionStatus), possessionDate: text(f.possessionDate),
        amenities: f.amenities, coverImage: f.photos[0] || null, images: f.photos.slice(1), videoUrl: text(f.videoUrl),
        brochureFile: text(f.brochureFile), brokerage: text(f.brokerage),
    };
};

const ProjectEditor = ({broker}) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = !id;
    const [project, setProject] = useState(null);
    const [form, setForm] = useState({...EMPTY, developerName: broker.companyName || ""});
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({loading: !isNew, saving: false, error: "", saved: ""});

    const load = useCallback(async () => {
        const p = await getMyProject(id);
        setProject(p);
        setForm(toForm(p));
        return p;
    }, [id]);

    useEffect(() => {
        if (isNew) return;
        load()
            .then(() => setStatus((s) => ({...s, loading: false})))
            .catch((err) => setStatus({loading: false, saving: false, error: err.message, saved: ""}));
    }, [isNew, load]);

    const set = (k) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({...f, [k]: value}));
        setErrors((er) => ({...er, [k]: undefined}));
    };

    const review = project?.reviewStatus || "DRAFT";
    const canSubmit = ["DRAFT", "CHANGES_REQUESTED"].includes(review);
    const live = review === "APPROVED";
    const pricedUnits = (project?.units || []).filter((u) => u.priceRaw).length;

    const save = async (submit) => {
        const found = validateProject(form, {submit, unitCount: pricedUnits});
        setErrors(found);
        if (Object.keys(found).length) {
            const fields = Object.keys(found).length;
            setStatus((s) => ({...s, error: fields === 1 ? Object.values(found)[0] : `Please fix the ${fields} highlighted fields.`, saved: ""}));
            setTimeout(() => document.querySelector(".brk-field--error, .brk-units--error")?.scrollIntoView({behavior: "smooth", block: "center"}), 50);
            return;
        }
        setStatus({loading: false, saving: true, error: "", saved: ""});
        try {
            let saved = isNew ? await createProject(toPayload(form)) : await updateProject(id, toPayload(form));
            if (submit) saved = await submitProject(saved.id);
            setProject(saved);
            setForm(toForm(saved));
            const message = submit ? "Submitted for review, with its unit types. We'll email you when it's decided."
                : isNew ? "Project saved. Now add its unit types below."
                    : live && saved.pendingChanges ? "Saved. Your changes go live once our team reviews them." : "Saved.";
            setStatus({loading: false, saving: false, error: "", saved: message});
            if (isNew) navigate(`/broker/projects/${saved.id}`, {replace: true});
            window.scrollTo({top: 0, behavior: "smooth"});
        } catch (err) {
            setStatus({loading: false, saving: false, error: err.message, saved: ""});
        }
    };

    if (status.loading) return <div className="brk-loading">Loading project…</div>;
    if (broker.status !== "APPROVED") {
        return <div className="brk-card"><p>You can add projects once your account is approved.</p>
            <Link className="brk-link" to="/broker/dashboard">Back to dashboard</Link></div>;
    }
    if (review === "REJECTED") {
        return <div className="brk-card"><ReviewNotice status="REJECTED" note={project.reviewNote} what="project"/>
            <Link className="brk-link" to="/broker/dashboard?tab=projects">Back to projects</Link></div>;
    }

    return (
        <form className="brk-editor" onSubmit={(e) => { e.preventDefault(); save(false); }} noValidate>
            <div className="brk-page-head">
                <div>
                    <Link to="/broker/dashboard?tab=projects" className="brk-back">← Projects</Link>
                    <h1 className="brk-page-title">{isNew ? "New project" : form.name || "Project"}</h1>
                </div>
                {project && <StatusBadge status={project.reviewStatus} pendingChanges={project.pendingChanges}/>}
            </div>
            {project && <ReviewNotice status={project.reviewStatus} note={project.reviewNote} pendingChanges={project.pendingChanges} what="project"/>}
            {status.saved && <div className="brk-notice brk-notice--ok" role="status">{status.saved}</div>}

            <Section title="Project" subtitle="Shared by every unit type: buyers see these on each one.">
                <Field label="Project name" required error={errors.name}><input className="brk-input" value={form.name} onChange={set("name")} maxLength={200}/></Field>
                <Field label="Developer"><input className="brk-input" value={form.developerName} onChange={set("developerName")} maxLength={100}/></Field>
                <Field label="Area / locality" required error={errors.location} hint="e.g. Whitefield"><input className="brk-input" value={form.location} onChange={set("location")} maxLength={150}/></Field>
                <Field label="Site address"><input className="brk-input" value={form.address} onChange={set("address")}/></Field>
                <Field label="Map position" error={errors.coordinates} wide
                       hint="Paste the site's Google Maps link or “12.9121, 77.6446”. Left empty, we place it from the address.">
                    <input className="brk-input" value={form.coordinates} onChange={set("coordinates")} placeholder="Latitude, longitude"/>
                </Field>
                <Field label="Description" wide hint={`${form.description.length}/5000`}>
                    <textarea className="brk-input" rows={5} maxLength={5000} value={form.description} onChange={set("description")}
                              placeholder="Highlights: towers, open space, clubhouse, connectivity to offices, schools and the metro…"/>
                </Field>
            </Section>

            <Section title="Approvals & scale">
                <Field label="Project RERA number" error={errors.reraNumber} hint="Projects with a RERA number are marked RERA approved">
                    <input className="brk-input" value={form.reraNumber} onChange={set("reraNumber")} placeholder="PRM/KA/RERA/1251/…/PR/…"/>
                </Field>
                <Field label="Khata"><input className="brk-input" value={form.kathaStatus} onChange={set("kathaStatus")} placeholder="e.g. A Khata"/></Field>
                <Field label="Possession">
                    <select className="brk-input" value={form.possessionStatus} onChange={set("possessionStatus")}>
                        <option value="">—</option>{POSSESSION.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                </Field>
                <Field label="Possession date"><input className="brk-input" type="date" value={form.possessionDate} onChange={set("possessionDate")}/></Field>
                <Field label="Total land area"><input className="brk-input" value={form.totalProjectArea} onChange={set("totalProjectArea")} placeholder="e.g. 5 acres"/></Field>
                <Field label="Towers / blocks"><input className="brk-input" type="number" min="0" value={form.blockCount} onChange={set("blockCount")}/></Field>
                <Field label="Floors per tower"><input className="brk-input" type="number" min="0" value={form.totalFloors} onChange={set("totalFloors")}/></Field>
                <Field label="Lifts per tower"><input className="brk-input" type="number" min="0" value={form.liftCount} onChange={set("liftCount")}/></Field>
                <Field label="Total units"><input className="brk-input" type="number" min="0" value={form.totalUnits} onChange={set("totalUnits")}/></Field>
                <Field label="Brokerage"><input className="brk-input" value={form.brokerage} onChange={set("brokerage")} placeholder="e.g. No brokerage"/></Field>
            </Section>

            <Section title="Amenities">
                <Field label="Select all that apply" wide group>
                    <ChipPicker options={COMMON_AMENITIES} value={form.amenities} onChange={set("amenities")}/>
                </Field>
            </Section>

            <Section title="Photos & brochure" subtitle="The first photo is the project's cover on every unit type.">
                <Field label="Photos" wide group required error={errors.photos}>
                    <PhotoManager value={form.photos} onChange={set("photos")} max={30}/>
                </Field>
                <Field label="Brochure (PDF)" group hint="Buyers download it from each unit's page; every download is a lead.">
                    <DocumentUpload value={form.brochureFile} onChange={set("brochureFile")} label="Upload brochure" accept="application/pdf"/>
                </Field>
                <Field label="Video link" error={errors.videoUrl}><input className="brk-input" value={form.videoUrl} onChange={set("videoUrl")} placeholder="https://"/></Field>
            </Section>

            {project ? (
                <UnitTypes project={project} error={errors.units} onChanged={async () => {
                    await load();
                    setErrors((er) => ({...er, units: undefined}));
                    setStatus((st) => ({...st, error: ""}));
                }}/>
            ) : (
                <div className="brk-notice brk-notice--info">Save the project first, then add its unit types (2 BHK, 3 BHK…).</div>
            )}

            <div className="brk-form-foot brk-form-foot--sticky">
                {status.error && <p className="brk-form-error" role="alert">⚠ {status.error}</p>}
                <Link to="/broker/dashboard?tab=projects" className="brk-btn brk-btn--ghost">Back</Link>
                <button type="submit" className="brk-btn brk-btn--ghost" disabled={status.saving}>
                    {status.saving ? "Saving…" : isNew ? "Save project" : live ? "Save changes" : "Save draft"}
                </button>
                {project && canSubmit && (
                    <button type="button" className="brk-btn brk-btn--primary" disabled={status.saving} onClick={() => save(true)}>
                        Submit project for review
                    </button>
                )}
            </div>
        </form>
    );
};

const ProjectEditorPage = () => <Workspace>{({broker}) => <ProjectEditor broker={broker}/>}</Workspace>;

export default ProjectEditorPage;
